import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Spinner,
} from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import type { Content, Editor } from "@tiptap/react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ProjectStatus,
  useGetProjectsQuery,
  type SourcingEventDto,
  type SourcingEventPatchDto, AiSuggestionItemType } from "../../../../../../autogen/bff-api";
import { AiDraftModal } from "../../../../../../common/ai/AiDraftModal";
import { useEditor, useFieldErrorTranslation } from "../../../../../../common/editing/save-coordinator";
import { FieldSaveErrorMessage, useFieldSaveError } from "../../components/FieldSaveError";
import { DateTimeSelector } from "../../../../../../common/input/DateTimeSelectors/DateTimeSelector";
import { TipTap, parseTiptapContent } from "../../../../../../common/input/TipTap/TipTap";
import { getMarkdownFromTiptapContent } from "../../../../../../common/input/TipTap/markdownUtils";
import { StepNavigation } from "../../../../../../common/StepNavigation";
import { useSub } from "../../../../../../common/subscription/useSub";
import { ProjectsSelector } from "../../../../../projects/ProjectsSelector";
import { buildPrivateDescriptionMessages } from "../../utils/descriptionAiPrompt";
import { ContractDurationField } from "../../components/ContractDurationField";
import { InheritedTermsCard } from "../mini-competition/InheritedTermsCard";

interface Props {
  eventId: string;
  previousStep?: () => void;
  nextStep?: () => void;
  nextStepLabel?: string;
}

export const DetailsStep = ({ eventId, previousStep, nextStep, nextStepLabel }: Props) => {
  const { t, i18n } = useTranslation();
  const sub = useSub();
  const { state, updateField, fieldStatus, fieldError } = useEditor<SourcingEventDto, SourcingEventPatchDto>(eventId);
  const formatFieldError = useFieldErrorTranslation();
  const { data: projectsData } = useGetProjectsQuery(
    sub.hasProjectAccess
      ? { status: [ProjectStatus.NotStarted, ProjectStatus.Ongoing].join(","), limit: 100 }
      : skipToken,
  );

  const descriptionEditorRef = useRef<Editor | null>(null);
  const [showDescriptionAi, setShowDescriptionAi] = useState(false);

  const invitedContext =
    state.eventContext.type === "Private" || state.eventContext.type === "MiniCompetition"
      ? state.eventContext
      : null;
  const description = state.eventData.description ?? "";

  const isSaving = (field: string) => fieldStatus(field) === "saving" || fieldStatus(field) === "pending";
  const descriptionSaveError = useFieldSaveError(eventId, "eventData.description");
  const referenceIdError = fieldError("eventData.referenceId");
  const deadlineError = fieldError("eventData.bidSubmissionDeadline");

  const selectedProjects = (state.eventData.projects ?? []).map((projectId) => {
    const project = projectsData?.projects.find((p) => p.id === projectId);
    return {
      label: project ? `${project.externalId ? `${project.externalId} - ` : ""}${project.name}` : projectId,
      value: projectId,
    };
  });

  return (
    <Box pb="8" pt="10">
      {state.eventContext.type === "MiniCompetition" && (
        <InheritedTermsCard frameworkAgreementId={state.eventContext.frameworkAgreementId} />
      )}
      <Box borderWidth="1px" borderRadius="lg" p="5" mb="8">
        <FormControl mb="6" isInvalid={!!descriptionSaveError}>
          <Flex align="center" gap="1">
            <FormLabel mb="0">{t("Description")}</FormLabel>
            {isSaving("eventData.description") && <Spinner size="xs" />}
          </Flex>
          <TipTap
            content={description}
            onUpdate={(value) => updateField({ eventData: { description: value } })}
            editable={true}
            debounceWait={300}
            onEditorReady={(editor) => {
              descriptionEditorRef.current = editor;
            }}
            onAutofill={() => setShowDescriptionAi(true)}
            askFurtherAbout={{ source: t("Description") }}
          />
          {descriptionSaveError ? (
            <FieldSaveErrorMessage error={descriptionSaveError} />
          ) : (
            <FormHelperText>{t("Describe the scope and requirements of this procurement")}</FormHelperText>
          )}
          {showDescriptionAi && (
            <AiDraftModal
              title={t("Suggest description with AI")}
              feature="se-private-description"
              insertsInto={{ itemType: AiSuggestionItemType.Procurement, itemId: eventId }}
              buildMessages={() =>
                buildPrivateDescriptionMessages({
                  title: state.eventData.title,
                  currentDescription: getMarkdownFromTiptapContent(description),
                  contractType: invitedContext?.contractNature ?? undefined,
                  estimatedValueNok: invitedContext?.estimatedValue?.value ?? null,
                  language: i18n.language.startsWith("no") ? "no" : "en",
                })
              }
              onClose={() => setShowDescriptionAi(false)}
              onInsert={(json) => {
                descriptionEditorRef.current?.commands.setContent(parseTiptapContent(json) as Content, {
                  emitUpdate: true,
                });
                setShowDescriptionAi(false);
              }}
            />
          )}
        </FormControl>

        <FormControl mb="6" isInvalid={!!referenceIdError}>
          <Flex align="center" gap="1">
            <FormLabel mb="0">{t("Reference id")}</FormLabel>
            {isSaving("eventData.referenceId") && <Spinner size="xs" />}
          </Flex>
          <Input
            type="text"
            value={state.eventData.referenceId ?? ""}
            onChange={(e) => {
              const trimmed = e.target.value.trim();
              updateField({ eventData: { referenceId: trimmed.length === 0 ? null : e.target.value } }, { debounceMs: 300 });
            }}
          />
          {referenceIdError ? (
            <FormErrorMessage>{formatFieldError(referenceIdError)}</FormErrorMessage>
          ) : (
            <FormHelperText>{t("Optional internal reference for this procurement")}</FormHelperText>
          )}
        </FormControl>

        <FormControl mb="6" isInvalid={!!deadlineError}>
          <Flex align="center" gap="1">
            <FormLabel mb="0">{t("Deadline")}</FormLabel>
            {isSaving("eventData.bidSubmissionDeadline") && <Spinner size="xs" />}
          </Flex>
          <DateTimeSelector
            date={state.eventData.bidSubmissionDeadline ?? null}
            onChange={(date) =>
              updateField({
                eventData: { bidSubmissionDeadline: date ?? null, bidSubmissionDeadlineConfirmed: date != null },
              })
            }
          />
          {deadlineError ? (
            <FormErrorMessage>{formatFieldError(deadlineError)}</FormErrorMessage>
          ) : (
            <FormHelperText>{t("The deadline for suppliers to submit their bids")}</FormHelperText>
          )}
        </FormControl>

        {sub.hasProjectAccess && (
          <FormControl>
            <FormLabel>{t("Projects")}</FormLabel>
            <ProjectsSelector
              selectedProjects={selectedProjects}
              onChange={(values) => updateField({ eventData: { projects: values.map((v) => v.value) } })}
            />
            <FormHelperText>{t("Select one or more projects the sourcing event is connected to")}.</FormHelperText>
          </FormControl>
        )}

        {/* FOA § 26-1: a call-off runs on its own term, which may outlast the framework agreement. */}
        {state.eventContext.type === "MiniCompetition" && (
          <FormControl>
            <FormLabel>{t("Contract duration")}</FormLabel>
            <ContractDurationField eventId={eventId} />
            <FormHelperText>
              {t("How long the contract from this mini-competition runs.")}
            </FormHelperText>
          </FormControl>
        )}
      </Box>

      <StepNavigation onPrevious={previousStep} onNext={nextStep} nextLabel={nextStepLabel} />
    </Box>
  );
};
